import {executeProposalOutcome} from './executeProposalOutcome';
import {navigateAfterExecute} from './navigateAfterExecute';
import {mergeCurrentFormValues} from './agentFormValues';
import {
    isRepeatOfCreatedDraft,
    releaseSessionFlow,
    rememberCreatedDraft,
    rememberProposalContext,
} from './agentSessionMemory';
import type {NavigateFn} from './navigateAfterExecute';
import type {ExecuteAiAgentProposalParams} from '../../../redux/aiAgent/aiAgentActions';
import type {AiAgentPendingProposal} from '../../../redux/aiAgent/aiAgent.type';
import type {DraftResult} from '../../aiMessageGenerator/utils/types';

export type AiAgentProposalOptions = {
    actionFailed: string;
    createdOpenFailed: string;
    droppedNotice: (fields: string[]) => string;
};

export type RunAgentProposalStatus = 'executed' | 'repeat' | 'failed' | 'refused' | 'openFailed';

export type RunAgentProposalResult = DraftResult & {
    status: RunAgentProposalStatus;
    /** What the record was created without — said next to whatever the turn answers. */
    notice?: string;
};

type RunAgentProposalParams = {
    proposal: AiAgentPendingProposal;
    agentText: string;
    execute: Omit<ExecuteAiAgentProposalParams, 'proposal'>;
    navigate: NavigateFn;
    options: AiAgentProposalOptions;
};

/** The agent's own wording stays, with the notice under it. */
function withAgentText(agentText: string, notice: string): string {
    return agentText ? `${agentText}\n\n${notice}` : notice;
}

/**
 * The turn created nothing, so the values it collected have to stay known — the next turn
 * sends only the change, and the record has to be complete enough to create.
 */
function keepProposalFields(
    proposal: AiAgentPendingProposal,
    text: string,
    status: Exclude<RunAgentProposalStatus, 'executed' | 'openFailed'>,
): RunAgentProposalResult {
    mergeCurrentFormValues(proposal.fields);
    return {text, status};
}

export async function runAgentProposal({
    proposal,
    agentText,
    execute,
    navigate,
    options,
}: RunAgentProposalParams): Promise<RunAgentProposalResult> {
    rememberProposalContext(proposal);

    if(isRepeatOfCreatedDraft(proposal)) {
        releaseSessionFlow();
        return {text: agentText, status: 'repeat'};
    }

    const {result, refusal} = await executeProposalOutcome({...execute, proposal});
    // The server refused on purpose and said why — the producer reads that instead of
    // "try again", and the flow ends here: there is nothing left to confirm.
    if(refusal) {
        releaseSessionFlow();
        return {text: withAgentText(agentText, refusal), status: 'refused'};
    }
    if(!result) {
        return keepProposalFields(proposal, withAgentText(agentText, options.actionFailed), 'failed');
    }

    let openFailed = false;
    try {
        await navigateAfterExecute(result, navigate);
    } catch {
        openFailed = true;
    }
    // After the wizard reset, so the created values are the ones the chat keeps. Also runs
    // when opening failed: the draft exists, and a next turn repeating the same values must
    // read as a repeat instead of creating a second one.
    rememberCreatedDraft(proposal.fields, result.eid);
    // The record exists whether or not its form opened, so the next request is free to be
    // another intent — a ticket for the event just created.
    releaseSessionFlow();

    const notice = result.dropped?.length ? options.droppedNotice(result.dropped) : undefined;
    if(openFailed) {
        return {text: withAgentText(agentText, options.createdOpenFailed), status: 'openFailed', notice};
    }
    return {text: agentText, status: 'executed', notice};
}
